/**
 * RopChart — on-bottom ROP against measured depth for the selected run's
 * bit-ops grid. A single themed line (ChartFrame → lazy Plotly). ROP is a
 * server-computed value; the chart only plots what the endpoint served.
 */
import { useMemo } from "react";
import type { Data } from "plotly.js-dist-min";
import { ChartFrame } from "@components/chart";
import type { BitOpOut } from "@api/types";
import { useLocale } from "@hooks/useLocale";

interface Props {
  ops: BitOpOut[];
  /** Axis unit labels resolved from the display profile (ft / m, ft/hr / m/hr). */
  depthUnit: string;
  ropUnit: string;
  subtitle?: string;
}

export function RopChart({ ops, depthUnit, ropUnit, subtitle }: Props) {
  const { t } = useLocale();
  const sorted = useMemo(() => [...ops].sort((a, b) => a.md - b.md), [ops]);

  const data = useMemo<Data[]>(
    () => [
      {
        type: "scatter",
        mode: "lines+markers",
        x: sorted.map((o) => o.md),
        y: sorted.map((o) => o.rop),
        name: t("bha.op.rop"),
        line: { width: 2 },
        marker: { size: 6 },
        hovertemplate: `%{y} ${ropUnit}<br>%{x} ${depthUnit}<extra></extra>`,
      },
    ],
    [sorted, t, ropUnit, depthUnit],
  );

  return (
    <ChartFrame
      title={t("bha.ropVsDepth")}
      subtitle={subtitle}
      data={data}
      xTitle={`${t("bha.op.md")} (${depthUnit})`}
      yTitle={`${t("bha.op.rop")} (${ropUnit})`}
      height={240}
      ariaLabel="On-bottom ROP versus measured depth for the current run"
    />
  );
}
